import React, { useCallback, useEffect, useMemo } from 'react';
import { RouteComponentProps } from 'react-router';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { ClientDatasetsConfigure } from './ClientDatasetsConfigure';
import { ClientDatasetsPreview } from './ClientDatasetsPreview';
import { fetchAllClientDatasetsStarted, fetchClientDatasetDefinitionsStarted, getAllClientDatasets, getClientDatasetDefinitions, getIsFetchingClientDatasets, getClientDatasetsView, getSelectedDatasetId, ClientDatasetView, resetClientDatasets, setClientDatasetView } from './store';
import { fetchAllDropdownListsStarted } from '../dropdown-lists/store';
import { fetchAllDocumentNamesStarted } from '../documents/store';
import { DatasetsList } from '../shared/DatasetsList';

export interface ClientDatasetsRouteParams {
    datasetId: string | undefined;
}

export const ClientDatasets: React.FC<RouteComponentProps<ClientDatasetsRouteParams>> = (props) => {
    const dispatch = useAppDispatch();

    useFetchStarted([
        fetchAllClientDatasetsStarted(),
        fetchAllDropdownListsStarted(),
        fetchAllDocumentNamesStarted()
    ]);
    const datasetId = useMemo(() => props.match.params.datasetId ? parseInt(props.match.params.datasetId) : undefined, [props]);

    const datasets = useAppSelector(getAllClientDatasets);
    const clientDatasetsView = useAppSelector(getClientDatasetsView);
    const parentDatasetId = useAppSelector(getSelectedDatasetId);
    const datasetDefinitions = useAppSelector(getClientDatasetDefinitions);
    const isFetchingDatasets = useAppSelector(getIsFetchingClientDatasets);
    const parentDefinition = useMemo(() => datasetDefinitions.map(definition => definition).find(({ datasetId }) => datasetId === parentDatasetId) || null, [datasetDefinitions, parentDatasetId]);

    const datasetIdInParamsExists = useMemo(() => (datasetId && datasets.map(({ datasetId }) => datasetId).includes(datasetId)) || false, [datasets, datasetId]);
    const urlDatasetView = useMemo(() => {
        if (props.match.url && props.match.url.includes('configure')) {
            return ClientDatasetView.CONFIGURE;
        }
        if (props.match.url && props.match.url.includes('preview')) {
            return ClientDatasetView.PREVIEW;
        }
        return ClientDatasetView.LIST;
    }, [props]);

    const fetchDatasetDefinitions = useCallback((datasetId: number) => dispatch(fetchClientDatasetDefinitionsStarted(datasetId)), [dispatch]);

    const openPreview = useCallback((datasetId: number) => {
        fetchDatasetDefinitions(datasetId);
        dispatch(setClientDatasetView(ClientDatasetView.PREVIEW, datasetId));
    }, [dispatch, fetchDatasetDefinitions]);

    const openConfigure = useCallback((datasetId: number) => {
        fetchDatasetDefinitions(datasetId);
        dispatch(setClientDatasetView(ClientDatasetView.CONFIGURE, datasetId));
    }, [dispatch, fetchDatasetDefinitions]);

    useEffect(() => {
        if (!parentDefinition && datasetIdInParamsExists) {
            dispatch(fetchClientDatasetDefinitionsStarted(datasetId!));
            dispatch(setClientDatasetView(urlDatasetView, datasetId!, false));
        }
    }, [dispatch, datasetIdInParamsExists, urlDatasetView, parentDefinition, datasetId]);

    useEffect(() => () => { dispatch(resetClientDatasets()); }, [dispatch]);

    const pageContent = useMemo(() => {
        switch (clientDatasetsView) {
            case ClientDatasetView.LIST:
            default:
                return <DatasetsList datasets={datasets} header='Datasets' isLoading={isFetchingDatasets} openPreview={openPreview} openConfigure={openConfigure} />;
            case ClientDatasetView.PREVIEW:
                return <ClientDatasetsPreview datasetId={datasetId!} />;
            case ClientDatasetView.CONFIGURE:
                return <ClientDatasetsConfigure datasetId={datasetId!} />;
        }
    }, [clientDatasetsView, datasets, datasetId, openPreview, openConfigure, isFetchingDatasets]);

    return (
        <div style={{ height: '100%', width: '100%' }}>{pageContent}</div>
    );
};

